import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { SEOHead } from "@/components/SEOHead";
import { 
  CheckCircle, 
  AlertCircle, 
  User, 
  Mail, 
  Shield,
  Database,
  TestTube
} from "lucide-react";
import { toast } from "sonner";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const TestAuth = () => {
  const { user, loading, signIn, signUp, signOut } = useAuth();
  const [testResults, setTestResults] = useState<Array<{ test: string; status: 'success' | 'error' | 'loading'; message: string }>>([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  // Test registration
  const testRegistration = async () => {
    const testEmail = `test-${Date.now()}@example.com`;
    const testPassword = "TestPassword123!";

    try {
      const { data, error } = await signUp(testEmail, testPassword, { role: 'buyer' });
      
      if (error) {
        return { status: 'error', message: error.message };
      }

      if (data.user && !data.user.email_confirmed_at) {
        return { 
          status: 'success', 
          message: `Registration successful! Confirmation email sent to ${testEmail}. Check your email to verify.` 
        };
      }

      return { status: 'success', message: 'Registration successful and email verified!' };
    } catch (err: any) {
      return { status: 'error', message: err.message || 'Registration failed' };
    }
  };

  // Test login with existing user
  const testLogin = async () => {
    try {
      // Try to login with the existing user from database
      const { data, error } = await signIn("ejoshuabain1@gmail.com", "test123");
      
      if (error) {
        return { status: 'error', message: error.message };
      }

      if (data.user) {
        // Sign out immediately to not affect current session
        await signOut();
        return { status: 'success', message: 'Login test successful!' };
      }

      return { status: 'error', message: 'Login succeeded but no user data returned' };
    } catch (err: any) {
      return { status: 'error', message: err.message || 'Login test failed' };
    }
  };

  // Test database connection
  const testDatabase = async () => {
    try {
      const { data, error } = await supabase.from('auth.users').select('id').limit(1);
      
      if (error) {
        return { status: 'error', message: error.message };
      }

      return { status: 'success', message: `Database connection successful! Found ${data?.length || 0} users` };
    } catch (err: any) {
      return { status: 'error', message: err.message || 'Database test failed' };
    }
  };

  // Test Supabase configuration
  const testSupabaseConfig = async () => {
    try {
      if (!isSupabaseConfigured) {
        return { status: 'error', message: 'Supabase is not configured' };
      }

      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        return { status: 'error', message: error.message };
      }

      return { status: 'success', message: 'Supabase configuration is valid' };
    } catch (err: any) {
      return { status: 'error', message: err.message || 'Config test failed' };
    }
  };

  // Run all tests
  const runAllTests = async () => {
    setIsRunningTests(true);
    setTestResults([]);

    const tests = [
      { name: 'Supabase Configuration', test: testSupabaseConfig },
      { name: 'Database Connection', test: testDatabase },
      { name: 'Email Registration', test: testRegistration },
      { name: 'Email Login', test: testLogin },
    ];

    for (const { name, test } of tests) {
      setTestResults(prev => [...prev, { test: name, status: 'loading', message: 'Running...' }]);
      
      try {
        const result = await test();
        setTestResults(prev => prev.map(r => 
          r.test === name ? { ...r, status: result.status as any, message: result.message } : r
        ));
      } catch (err: any) {
        setTestResults(prev => prev.map(r => 
          r.test === name ? { ...r, status: 'error', message: err.message || 'Test failed' } : r
        ));
      }

      // Add delay between tests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setIsRunningTests(false);
  };

  // Manual login test
  const handleManualLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = String(formData.get('email') || '').trim();
    const password = String(formData.get('password') || '');

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const { data, error } = await signIn(email, password);

      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success('Manual login successful!');
    } catch (err: any) {
      toast.error(err.message || 'Login failed');
    }
  };

  // Manual registration test
  const handleManualRegistration = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = String(formData.get('regEmail') || '').trim();
    const password = String(formData.get('regPassword') || '');

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const { data, error } = await signUp(email, password, { role: 'buyer' });

      if (error) {
        toast.error(error.message);
        return;
      }

      if (data.user?.email_confirmed_at) {
        toast.success('Registration successful and email verified!');
      } else {
        toast.success('Registration successful! Check your email for verification.');
      }
    } catch (err: any) {
      toast.error(err.message || 'Registration failed');
    }
  };

  return (
    <>
      <SEOHead
        title="Test Authentication - Bahama Marketplace"
        description="Test and debug authentication functionality."
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Header */}
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight mb-2">Authentication Testing</h1>
              <p className="text-muted-foreground">
                Test and debug email authentication functionality
              </p>
            </div>

            {/* Current User Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Current User Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    Loading user session...
                  </div>
                ) : user ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium">Signed in as: {user.email}</span>
                    </div>
                    <div className="grid gap-2 text-sm text-muted-foreground">
                      <div>User ID: {user.id}</div>
                      <div>Email Confirmed: {user.email_confirmed_at ? 'Yes' : 'No'}</div>
                      <div>Role: {user.user_metadata?.role || 'None'}</div>
                      <div>Last Sign In: {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'Never'}</div>
                    </div>
                    <Button onClick={signOut} variant="outline" size="sm">
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span>Not signed in</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Automated Tests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TestTube className="h-5 w-5" />
                  Automated Tests
                </CardTitle>
                <CardDescription>
                  Run automated tests to verify authentication system functionality
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Button 
                    onClick={runAllTests} 
                    disabled={isRunningTests}
                    className="w-full"
                  >
                    {isRunningTests ? 'Running Tests...' : 'Run All Tests'}
                  </Button>

                  {testResults.length > 0 && (
                    <div className="space-y-2">
                      {testResults.map((result, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                          {result.status === 'loading' && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          )}
                          {result.status === 'success' && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          {result.status === 'error' && (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          )}
                          <div className="flex-1">
                            <div className="font-medium">{result.test}</div>
                            <div className="text-sm text-muted-foreground">{result.message}</div>
                          </div>
                          <Badge variant={result.status === 'success' ? 'default' : result.status === 'error' ? 'destructive' : 'secondary'}>
                            {result.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Manual Tests */}
            <div className="grid gap-6 md:grid-cols-2">
              
              {/* Manual Login */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Manual Login Test
                  </CardTitle>
                  <CardDescription>
                    Test login with your credentials
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleManualLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your-email@example.com"
                        defaultValue="ejoshuabain1@gmail.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="password">Password</Label>
                      <Input 
                        id="password"
                        name="password"
                        type="password"
                        placeholder="Enter your password"
                      />
                    </div>
                    <Button type="submit" className="w-full">
                      Test Login
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Manual Registration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Manual Registration Test
                  </CardTitle>
                  <CardDescription>
                    Test registration with a new email
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleManualRegistration} className="space-y-4">
                    <div>
                      <Label htmlFor="regEmail">Email</Label>
                      <Input 
                        id="regEmail"
                        name="regEmail"
                        type="email"
                        placeholder={`test-${Date.now()}@example.com`}
                      />
                    </div>
                    <div>
                      <Label htmlFor="regPassword">Password</Label>
                      <Input 
                        id="regPassword"
                        name="regPassword"
                        type="password"
                        placeholder="TestPassword123!"
                        defaultValue="TestPassword123!"
                      />
                    </div>
                    <Button type="submit" className="w-full">
                      Test Registration
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* System Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  System Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span>Supabase Configured:</span>
                    <Badge variant={isSupabaseConfigured ? 'default' : 'destructive'}>
                      {isSupabaseConfigured ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Supabase URL:</span>
                    <span className="font-mono text-xs">{import.meta.env.VITE_SUPABASE_URL || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Anon Key:</span>
                    <span className="font-mono text-xs">
                      {import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Set' : 'Not set'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Help */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Having authentication issues?</strong> This page helps you debug common problems:
                <br />
                • Run automated tests to check system health
                • Test manual login/registration
                • Verify Supabase configuration
                <br />
                Common fixes: Check environment variables, verify email confirmation, ensure correct credentials.
              </AlertDescription>
            </Alert>

          </div>
        </div>
      </div>
    </>
  );
};

export default TestAuth;